const _ = require("lodash");
const Poset = require("../../util/Poset");
const {polygonIntersection, isInPoly} = require("../../util/Math2D");
const {
	multiplyMV2,
	multiplyMV4,
	multiplyMM4,
	invertM2,
} = require("../../util/MatrixUtil");
const {vToKey, getCommonPoints} = require("./VOverlap");
// Alias V as V2 in this file. In a file for 3D, V meaning 2D is confusing.
const {V: V2, V3} = require("./VectorDraw");

// Logs extra things to help diagnose face ordering wrongness.
const DEBUG_ORDERING = false;

const isClose = (x, y) => Math.abs(x - y) < 0.00000001;

// Given a 2D point, return the z coordinate it would have if it were lying on
// the plane of the given list of vertices.
const computeZOnFace = function(p, vs) {
	const planeV1 = vs[1].sub(vs[0]);
	const pV = p.sub(vs[0]);
	if (!vs[2]) {
		// vs is an edge. We assume p is a point on it when extended.
		const t = isClose(pV[0], planeV1[0])
			? pV[1] / planeV1[1]
			: pV[0] / planeV1[0];
		return vs[0][2] + t * planeV1[2];
	}
	const planeV2 = vs[2].sub(vs[0]);
	const invBasisM = invertM2([planeV1[0], planeV2[0], planeV1[1], planeV2[1]]);
	if (!_.isFinite(invBasisM[0])) {
		throw new Error("Tried to compute z of point on a degenerate face");
	}
	const [v1Comp, v2Comp] = multiplyMV2(invBasisM, pV);
	return vs[0][2] + planeV1[2] * v1Comp + planeV2[2] * v2Comp;
};

const TINY_ANGLE = 0.01;
const TINY_DIST = 0.001;
// Given two 2D faces (array of V2s) with a specified common vertex, return a
// point in the interior of both faces. Used to find draw ordering of faces.
const findInteriorPolyIntersection = function(p1, p2, commonV) {
	const commonInd1 = _.findIndex(p1, v => vToKey(v) === vToKey(commonV));
	const commonInd2 = _.findIndex(p2, v => vToKey(v) === vToKey(commonV));
	// Both of these should be greater than -1 if commonV is passed correctly.
	if (commonInd1 === -1) {
		throw new Error(
			"common vertex passed to findInteriorPolyIntersection not in face 1"
		);
	} else if (commonInd2 === -1) {
		throw new Error(
			"common vertex passed to findInteriorPolyIntersection not in face 2"
		);
	}

	// Helper that takes a V2 and returns one close to commonV while still being
	// in the same direction from commonV.
	const getClose = v =>
		v
			.sub(commonV)
			.toMagnitude(TINY_DIST)
			.add(commonV);

	// First, deal with segments as a special case.
	if (p1.length === 2 && p2.length === 2) {
		return null;
	} else if (p1.length === 2) {
		// Take a point on segment p1 close to commonV. Use it if inside poly2,
		// else assume no intersection.
		const closeV = getClose(p1[(commonInd1 + 1) % 2]);
		return isInPoly(p2, closeV[0], closeV[1]) ? closeV : null;
	} else if (p2.length === 2) {
		const closeV = getClose(p2[(commonInd2 + 1) % 2]);
		return isInPoly(p1, closeV[0], closeV[1]) ? closeV : null;
	}

	// Strategy: Take a point very close to commonV in direction of each of the
	// four neighbors, but rotated slightly. Test if it's in both
	// polygons. Take any of them that are in both, else return no intersection.

	// This helper returns the close point, slightly rotated.
	const getCloseRotated = neighborV => {
		return getClose(neighborV.rotate(TINY_ANGLE, commonV));
	};
	// Tests if, for a neighbor, its close rotated point is in both polygons.
	// Returns that point if yes, null if no.
	const testNeighbor = neighborV => {
		const closeV = getCloseRotated(neighborV);
		if (
			isInPoly(p1, closeV[0], closeV[1]) &&
			isInPoly(p2, closeV[0], closeV[1])
		) {
			return closeV;
		}
		return null;
	};

	// Four neighbors of commonV between both polygons.
	const prevV1 = p1[(commonInd1 + p1.length - 1) % p1.length];
	const prevV2 = p2[(commonInd2 + p2.length - 1) % p2.length];
	const nextV1 = p1[(commonInd1 + 1) % p1.length];
	const nextV2 = p2[(commonInd2 + 1) % p2.length];
	// Test each one.
	return (
		testNeighbor(prevV1) ||
		testNeighbor(prevV2) ||
		testNeighbor(nextV1) ||
		testNeighbor(nextV2) ||
		null
	);
};

// A face is a {vs, int, priority} object. All vertices have been passed
// through the camera matrix but not the projection matrix.
// - vs is an array of 3-vectors, giving the vertices of the face. At least
//   two are needed, and behavior is undefined if not all are coplanar.
// - int is an optional 3-vector giving any interior point of the shape, for
//   backface detection. If not provided, or if less than three vertices, no
//   backface culling is done.
// - priority is an optional number, default 0, to hint at where in the draw
//   order to put this, in case there are close calls or not-quite-intersecting
//   segments. It will be ignored if an in-front-of relationship is detected.
//   Higher numbers mean to draw the face farther in front, lower in back.

const isBackface = function(face) {
	if (!face.int || face.vs.length < 3) {
		return false;
	}
	// First, we compute a normal vector of the face from a base vertex.
	const base = face.vs[0];
	const normal = V3.faceNormal(...face.vs);
	// We then project the interior vertex we're given onto the normal.
	const intOnNormal = face.int.sub(base).project(normal);
	const pointOnNormal = intOnNormal.add(base);
	// If that projection is closer than the base of the normal, backface!
	return pointOnNormal[2] > base[2];
};

// Handles the case of determining whether f1 or f2 are in front of each other
// when they have at least one common vertex, passed as commonV.
const isInFrontOfCommonVertex = function(f1, f2, commonV) {
	const f1TwoD = f1.vs.map(v => V2(v));
	const f2TwoD = f2.vs.map(v => V2(v));
	const intV = findInteriorPolyIntersection(f1TwoD, f2TwoD, V2(commonV));
	if (intV) {
		const z1 = computeZOnFace(intV, f1.vs);
		const z2 = computeZOnFace(intV, f2.vs);
		return isClose(z1, z2) ? null : z1 > z2;
	}
	return null;
};

// Handles the case of determining whether f1 or f2 are in front of each other
// via comparing z coordinates at a projected intersection, which only works
// when they have no common vertices.
const isInFrontOfIntersection = function(f1, f2) {
	// If no common vertices, find an intersection of the faces when they are
	// projected onto 2D, and compare the z values there.
	const f1TwoD = f1.vs.map(v => v.slice(0, 2));
	const f2TwoD = f2.vs.map(v => v.slice(0, 2));
	let intV = polygonIntersection(f1TwoD, f2TwoD);
	if (intV) {
		intV = V2(intV);
		const z1 = computeZOnFace(intV, f1.vs);
		const z2 = computeZOnFace(intV, f2.vs);
		return isClose(z1, z2) ? null : z1 > z2;
	}
	// Else, these two faces don't intersect when projected and are incomparable.
	return null;
};

// Given two faces, determine which one to draw first. Returns true if f1 is
// in front of f2, false if f2 is in front of f1, and null if incomparable.
const isInFrontOf = function(f1, f2) {
	// First, deal with special cases where there are common vertices.
	const commonVList = getCommonPoints(f1.vs, f2.vs);
	// Draw edges in front of faces that contain them.
	if (commonVList.length >= 2 && f1.vs.length <= 2) {
		return true;
	} else if (commonVList.length >= 2 && f2.vs.length <= 2) {
		return false;
	} else if (commonVList.length > 0) {
		return isInFrontOfCommonVertex(f1, f2, commonVList[0]);
	} else {
		return isInFrontOfIntersection(f1, f2);
	}
};

// Returns drawing order of faces, also filtered to exclude backfaces. Faces
// that are drawn behind all others come first. Note that the list can include
// edges, which are faces with two vertices.
const orderFaces = function(fList) {
	let LOGFACES;
	fList = fList.filter(f => !isBackface(f));
	if (DEBUG_ORDERING) {
		LOGFACES = function(fList2) {
			return fList2.map(f => f._id).join(", ");
		};
		fList.forEach((f, i) => {
			f._id = i;
		});
	}
	// Get a rough ordering by sorting objects by maximum z coordinate.
	const prelimOrderedFList = _.sortBy(fList, f => {
		return 100 * (f.priority || 0) + Math.max(...f.vs.map(x => x[2]));
	});
	if (DEBUG_ORDERING) {
		// eslint-disable-next-line no-console
		console.log("start", LOGFACES(prelimOrderedFList));
	}
	let cmp = isInFrontOf;
	if (DEBUG_ORDERING) {
		cmp = (f1, f2) => {
			const result = isInFrontOf(f1, f2);
			// eslint-disable-next-line no-console
			console.log("cmp", f1._id, f2._id, result);
			return result;
		};
	}
	const orderedFList = Poset.order(prelimOrderedFList, cmp);
	if (DEBUG_ORDERING) {
		// eslint-disable-next-line no-console
		console.log("done", LOGFACES(orderedFList));
	}
	return orderedFList;
};

// explicitDrawOrder implementation of ordering faces. Only based on priority.
const orderFacesExplicit = function(fList) {
	return _.sortBy(fList, f => f.priority || 0);
};

// Returns three 3-vectors, giving the axes of the camera in world space.
// See http://www.scratchapixel.com/lessons/3d-basic-rendering/computing-pixel-coordinates-of-3d-point/mathematics-computing-2d-coordinates-of-3d-points
const getCameraVectors = function(lookFrom, lookAt) {
	const camZVec = lookFrom.sub(lookAt).unit();
	const stdZOntoCamZ = V3(0, 0, 1).project(camZVec);
	let camYVec = V3(0, 0, 1)
		.sub(stdZOntoCamZ)
		.unit();
	if (_.isNaN(camYVec[0])) {
		camYVec = V3(0, camZVec[2], 0);
	}
	const camXVec = camYVec.cross(camZVec);
	return [camXVec, camYVec, camZVec];
};

// Return a world-to-camera matrix.
const makeCameraMatrix = function(lookFrom, lookAt) {
	const [camXVec, camYVec, camZVec] = getCameraVectors(lookFrom, lookAt);
	const invTransM = [
		1,
		0,
		0,
		-lookFrom[0],
		0,
		1,
		0,
		-lookFrom[1],
		0,
		0,
		1,
		-lookFrom[2],
		0,
		0,
		0,
		1,
	];
	const orientM = [...camXVec, 0, ...camYVec, 0, ...camZVec, 0, 0, 0, 0, 1];
	return multiplyMM4(orientM, invTransM);
};

// http://www.scratchapixel.com/lessons/3d-basic-rendering/perspective-and-orthographic-projection-matrix/building-basic-perspective-projection-matrix
const makeProjMatrix = function(f, n, fov) {
	const S = 1 / Math.tan(fov / 2);
	return [
		S,
		0,
		0,
		0,
		0,
		S,
		0,
		0,
		0,
		0,
		-f / (f - n),
		-(f * n) / (f - n),
		0,
		0,
		-1,
		0,
	];
};

const worldToProj = function(v, world) {
	const {cameraMatrix, projMatrix} = world;
	v = [v[0], v[1], v[2], 1];
	v = multiplyMV4(cameraMatrix, v);
	v = multiplyMV4(projMatrix, v);
	return V3(v[0] / v[3], v[1] / v[3], v[2] / v[3]);
};

const fromNDCToScreen = function(v, world) {
	const x = v[0];
	const y = v[1];
	const z = v[2];
	const {ndcCanvasRect, nearZ, farZ} = world;
	if (z < nearZ || z > farZ) {
		return null;
	}
	// From (-1,-1);(1,1) to ndcCanvasRect
	return V2(
		ndcCanvasRect[0] + ((x + 1) * ndcCanvasRect[2]) / 2,
		ndcCanvasRect[1] - ((y - 1) * ndcCanvasRect[3]) / 2
	);
};

// Returns square as [x,y,w,h] to use to convert from NDC coordinates to canvas
// coordinates. See description of fill and overscan here:
// http://www.scratchapixel.com/lessons/3d-basic-rendering/3d-viewing-pinhole-camera/how-pinhole-camera-works-part-2
const adjustRectToFill = function([x, y, w, h]) {
	if (w > h) {
		return [x, y - (w - h) / 2, w, w];
	} else {
		return [x - (h - w) / 2, y, h, h];
	}
};

const faceToNDC = function(f, world) {
	return _.extend({}, f, {
		vs: f.vs.map(v => worldToProj(v, world)),
		int: f.int && worldToProj(f.int, world),
	});
};

// All methods of World3D expect V2 or V3 objects where points need to be passed.

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const World3D = function(props) {
	const {
		lookFrom,
		lookAt,
		farZ = 1,
		nearZ = -1,
		fov,
		canvasRect,
		explicitDrawOrder = false,
	} = props;
	this.nearZ = nearZ;
	this.farZ = farZ;
	this.ndcCanvasRect = adjustRectToFill(canvasRect);
	this.canvasRect = canvasRect;
	this.explicitDrawOrder = explicitDrawOrder;

	this.cameraMatrix = makeCameraMatrix(lookFrom, lookAt);
	this.projMatrix = makeProjMatrix(farZ, nearZ, fov);
	this.drawFaceList = [];
	this.drawFuncList = [];
};

World3D.prototype.isBackface = function(f) {
	return isBackface(faceToNDC(f, this));
};

// Pass face in world coordinates as {vs, int, fill, stroke, strokeWidth, priority}.
// All are optional except vs. Priority defaults to 0. If explicitDrawOrder was
// set on construction, it is the sole determiner of draw order, with higher
// numbers meaning to draw later (in front).
World3D.prototype.addFace = function(f) {
	this.drawFaceList.push(f);
};

// Pass an arbitrary function to call after drawing. It will be passed the
// context.
World3D.prototype.addPostDraw = function(func) {
	this.drawFuncList.push(func);
};

World3D.prototype.toCanvas = function(v) {
	return fromNDCToScreen(worldToProj(v, this), this);
};

World3D.prototype.draw = function(ctx) {
	if (DEBUG_ORDERING) {
		this.drawFaceList.forEach((f, i) => {
			// eslint-disable-next-line no-console
			console.log("Face", i, ":", f.vs.map(vToKey).join(" ; "));
		});
	}
	const ndcFaceList = this.drawFaceList.map(f => faceToNDC(f, this));
	if (DEBUG_ORDERING) {
		ndcFaceList.forEach((f, i) => {
			// eslint-disable-next-line no-console
			console.log("NDC Face", i, ":", f.vs.map(vToKey).join(" ; "));
		});
	}
	ctx.save();
	ctx.bp();
	ctx.rect(...this.canvasRect);
	ctx.clip();
	if (ctx.lineCap === "butt") {
		ctx.lineCap = "round";
	}
	const orderImpl = this.explicitDrawOrder ? orderFacesExplicit : orderFaces;
	orderImpl(ndcFaceList).forEach(f => {
		const canvasVs = f.vs.map(v => fromNDCToScreen(v, this));
		if (canvasVs.indexOf(null) > -1) {
			// One point got clipped by nearZ or farZ planes. Since we draw entire
			// polygons without partial clipping, bail out.
			return; // TODO: some kind of error?
		}
		ctx.save();
		ctx.bp();
		canvasVs.forEach(v => ctx.L(v[0], v[1]));
		if (canvasVs.length > 2) {
			ctx.Z();
		}
		if (f.opacity) {
			ctx.globalAlpha = f.opacity;
		}
		if (f.fill) {
			ctx.fillStyle = f.fill;
			ctx.fill();
		}
		if (f.stroke) {
			ctx.strokeStyle = f.stroke;
			ctx.lineWidth = f.strokeWidth || ctx.lineWidth;
			if (f.lineDash) {
				ctx.lineCap = "butt";
				ctx.setLineDash(f.lineDash);
			}
			ctx.stroke();
		}
		ctx.restore();
	});
	this.drawFuncList.forEach(func => func(ctx));
	ctx.restore();
	this.drawFaceList = [];
	this.drawFuncList = [];
};

module.exports = World3D;
